/**
 * WCAG 2.1.1 Keyboard
 * Makes custom (non-native) controls focusable and operable with a keyboard.
 */
(function () {
  const NATIVE_SELECTOR =
    'a[href], button, input, select, textarea, summary, [contenteditable="true"]';

  function isNativeControl(el) {
    return el.matches(NATIVE_SELECTOR);
  }

  function isHidden(el) {
    return Boolean(el.closest('[aria-hidden="true"], [hidden], [inert]'));
  }

  function enhanceRoleButtons(root) {
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('[role="button"]').forEach((el) => {
      if (isNativeControl(el) || isHidden(el)) return;
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    });
  }

  function thumbIndex(thumbs) {
    const active = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
    return active < 0 ? 0 : active;
  }

  function enhanceGalleries() {
    document
      .querySelectorAll('.oh-gallery-ui, .oh-gallery-ui-2, .oh-gallery-ui-3x, .oh-gallery-ui-second')
      .forEach((gallery) => {
        gallery.removeAttribute('aria-hidden');

        const handle = gallery.querySelector('[role="button"]');
        const thumbs = Array.from(gallery.querySelectorAll('img[class*="thumb"]'));
        if (!handle || !thumbs.length) return;

        handle.setAttribute('tabindex', '0');
        handle.setAttribute('role', 'slider');
        handle.setAttribute('aria-orientation', 'horizontal');
        handle.setAttribute('aria-valuemin', '0');
        handle.setAttribute('aria-valuemax', String(thumbs.length - 1));
        handle.setAttribute('aria-valuenow', String(thumbIndex(thumbs)));
        if (!handle.getAttribute('aria-label')) {
          handle.setAttribute('aria-label', 'Cycle images');
        }

        thumbs.forEach((thumb, index) => {
          thumb.setAttribute('tabindex', '0');
          thumb.setAttribute('role', 'button');
          if (!thumb.getAttribute('aria-label')) {
            thumb.setAttribute('aria-label', thumb.getAttribute('alt') || 'Show image ' + (index + 1));
          }
        });

        if (handle.dataset.keyboardBound === 'true') return;
        handle.dataset.keyboardBound = 'true';

        handle.addEventListener('keydown', (event) => {
          const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();

          let index = thumbIndex(thumbs);
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            index = Math.min(thumbs.length - 1, index + 1);
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            index = Math.max(0, index - 1);
          } else if (event.key === 'Home') {
            index = 0;
          } else if (event.key === 'End') {
            index = thumbs.length - 1;
          }

          handle.setAttribute('aria-valuenow', String(index));
          thumbs[index].click();
        });
      });
  }

  function enhanceVariantPills() {
    document.querySelectorAll('.product-form__input input[type="radio"]').forEach((radio) => {
      radio.setAttribute('tabindex', '-1');
      const label = radio.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') return;

      if (radio.classList.contains('disabled') || radio.disabled) {
        label.setAttribute('tabindex', '-1');
        return;
      }

      label.setAttribute('tabindex', '0');
      if (label.dataset.keyboardBound === 'true') return;
      label.dataset.keyboardBound = 'true';

      label.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        radio.checked = true;
        radio.dispatchEvent(new Event('input', { bubbles: true }));
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.click();
      });
    });
  }

  function makeKeyboardButton(el) {
    if (!el || isHidden(el)) return;
    if (el.matches('a[href], button, input, select, textarea')) {
      if (el.getAttribute('tabindex') === '-1') el.setAttribute('tabindex', '0');
      return;
    }
    if (el.tagName === 'A' && !el.getAttribute('href')) {
      el.setAttribute('href', '#');
      el.addEventListener('click', (event) => {
        if (el.getAttribute('href') === '#') event.preventDefault();
      });
    }
    el.tabIndex = 0;
    if (!el.getAttribute('role')) el.setAttribute('role', 'button');
  }

  function enhanceWithinReviews(root) {
    const nodes = root.querySelectorAll('a, button, span, div, p, li');
    nodes.forEach((el) => {
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      const isYesNo = /^(Yes|No)\s*\(\d+\)$/i.test(text);
      const isShowMore = /^(Show more|Show less|Load more)$/i.test(text);
      if (!isYesNo && !isShowMore) return;
      const hasMatchingChild = Array.from(el.children).some((child) => {
        const childText = (child.textContent || '').replace(/\s+/g, ' ').trim();
        return childText === text;
      });
      if (hasMatchingChild) return;
      makeKeyboardButton(el);
    });
  }

  function enhanceKlaviyoReviews() {
    document
      .querySelectorAll(
        '[class*="kl_reviews"], [class*="klaviyo-reviews"], [class*="klaviyo_reviews"], [id*="klaviyo-reviews"], [data-klaviyo-reviews], .shopify-block, .shopify-app-block'
      )
      .forEach((root) => {
        const text = root.textContent || '';
        if (!/recommend this product|Show more|Show less/i.test(text)) return;
        enhanceWithinReviews(root);
      });

    document.querySelectorAll('*').forEach((el) => {
      if (!el.shadowRoot) return;
      const identity = (el.tagName || '') + ' ' + (el.className || '');
      if (!/klaviyo|kl_reviews|kl-reviews/i.test(identity)) return;
      enhanceWithinReviews(el.shadowRoot);
    });
  }

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    if (isNativeControl(target)) return;
    if (target.getAttribute('role') !== 'button') return;

    event.preventDefault();
    target.click();
  });

  function isSafariBrowser() {
    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    if (!/Apple/i.test(vendor)) return false;
    if (/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Android/i.test(ua)) return false;
    return /Safari/i.test(ua);
  }

  function inFocusTrap(el) {
    if (!(el instanceof Element)) return false;
    if (el.closest('dialog[open], details-modal[open], .menu-opening')) return true;
    const drawer = el.closest('cart-drawer, .cart-drawer, .drawer');
    return Boolean(drawer && drawer.classList.contains('active'));
  }

  function isClosedDetailsContent(el) {
    const details = el.closest('details');
    if (!details || details.open) return false;
    const summary = details.querySelector('summary');
    return el !== summary && !(summary && summary.contains(el));
  }

  function isReachable(el) {
    if (!(el instanceof Element)) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    if (el.matches(':disabled, [disabled], [hidden]')) return false;
    if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    if (isClosedDetailsContent(el)) return false;

    let node = el;
    while (node && node.nodeType === 1) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement || node.getRootNode().host || null;
    }
    return true;
  }

  const TABBABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let tabbableCache = null;

  function invalidateTabbables() {
    tabbableCache = null;
  }

  function collectTabbables(root, list) {
    root.querySelectorAll(TABBABLE_SELECTOR).forEach((el) => {
      if (!isReachable(el)) return;
      if (list.indexOf(el) === -1) list.push(el);
    });
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) collectTabbables(el.shadowRoot, list);
    });
  }

  function getTabbables() {
    if (tabbableCache) return tabbableCache;
    const list = [];
    collectTabbables(document, list);
    tabbableCache = list;
    return list;
  }

  function revealForKeyboard(el) {
    if (!(el instanceof Element)) return;
    let node = el;
    while (node && node.nodeType === 1) {
      if (node.classList && node.classList.contains('scroll-trigger')) {
        node.classList.remove('scroll-trigger--offscreen');
        node.classList.add('scroll-trigger--cancel');
      }
      node = node.parentElement;
    }
  }

  function openParentMenu(el) {
    const details = el.closest('details.mega-menu, header-menu details');
    if (details && !details.open) details.setAttribute('open', '');
  }

  if (isSafariBrowser()) {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Tab' || event.defaultPrevented || event.isComposing) return;
        const active =
          document.activeElement instanceof Element ? document.activeElement : null;
        if (inFocusTrap(active || document.body)) return;

        const items = getTabbables();
        if (!items.length) return;

        let index = active ? items.indexOf(active) : -1;
        if (index === -1 && active) {
          index = items.findIndex((el) => el.contains(active));
        }

        const nextIndex =
          index === -1
            ? event.shiftKey
              ? items.length - 1
              : 0
            : event.shiftKey
              ? (index - 1 + items.length) % items.length
              : (index + 1) % items.length;
        const next = items[nextIndex];
        if (!next || next === active) return;

        event.preventDefault();
        requestAnimationFrame(() => {
          openParentMenu(next);
          revealForKeyboard(next);
          if (typeof next.focus === 'function') next.focus();
          if (typeof next.scrollIntoView === 'function') {
            next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        });
      },
      true
    );

    document.addEventListener('toggle', invalidateTabbables, true);
    window.addEventListener('resize', invalidateTabbables);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    document.documentElement.classList.add('user-is-tabbing');
  });
  document.addEventListener('mousedown', () => {
    document.documentElement.classList.remove('user-is-tabbing');
  });

  document.addEventListener(
    'focusin',
    (event) => {
      if (!(event.target instanceof Element)) return;
      revealForKeyboard(event.target);
    },
    true
  );

  function init() {
    enhanceRoleButtons(document);
    enhanceGalleries();
    enhanceVariantPills();
    enhanceKlaviyoReviews();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  [500, 1500, 3000, 6000].forEach((delay) => {
    window.setTimeout(() => {
      enhanceKlaviyoReviews();
      enhanceVariantPills();
    }, delay);
  });

  const observer = new MutationObserver((mutations) => {
    invalidateTabbables();
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        enhanceRoleButtons(node);
        enhanceVariantPills();
        const markup = (typeof node.className === 'string' ? node.className : '') + (node.id || '');
        if (
          /klaviyo|kl_reviews/i.test(markup) ||
          (node.querySelector && node.querySelector('[class*="kl_reviews"], [class*="klaviyo"]'))
        ) {
          enhanceKlaviyoReviews();
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
